import {memo, useCallback} from "react";
import {useNavigate} from "react-router-dom";
import useGetTopReferrals from "@/hooks/referrals/useGetTopReferrals";
import useRequireAuth from "@/hooks/useRequireAuth";
import {useAuthStore} from "../../../store/useAuth.store";
import {avatarImages} from "../../../util/avatarUtils";
import styles from "./TopReferrals.module.css";

const RANK_STYLES = [styles.rankGold, styles.rankSilver, styles.rankBronze];
const ANIMATION_THRESHOLD = 5;

const getAvatarForName = name => {
  let hash = 0;
  for (const ch of name) hash = (hash * 31 + ch.charCodeAt(0)) | 0;
  return avatarImages[Math.abs(hash) % avatarImages.length];
};

const obfuscateAlias = alias => {
  if (!alias) return "";
  if (alias.length <= 2) return `${alias[0]}*`;
  if (alias.length <= 4) {
    return `${alias[0]}${"*".repeat(alias.length - 2)}${alias.at(-1)}`;
  }
  return `${alias.slice(0, 2)}${"*".repeat(alias.length - 3)}${alias.slice(-1)}`;
};

const ReferralRow = memo(({referrer, onNavigate}) => {
  const {alias, rank, referral_count: referralCount} = referrer;
  const maskedAlias = obfuscateAlias(alias);

  return (
    <div
      className={styles.row}
      onClick={() => onNavigate(alias)}
      onKeyDown={e => (e.key === "Enter" || e.key === " ") && onNavigate(alias)}
      role="link"
      tabIndex={0}
      aria-label={`Ver perfil de @${maskedAlias}`}
    >
      <span className={`${styles.rank} ${RANK_STYLES[rank - 1] || ""}`}>{rank}</span>
      <div className={styles.avatar}>
        <img
          src={getAvatarForName(alias)}
          alt={maskedAlias}
          className={styles.avatarImg}
          loading="lazy"
          onError={e => {
            e.currentTarget.style.display = "none";
            e.currentTarget.nextElementSibling.style.display = "flex";
          }}
        />
        <span
          className={styles.avatarFallback}
          style={{display: "none"}}
        >
          {alias.charAt(0).toUpperCase()}
        </span>
      </div>
      <span className={styles.userName}>@{maskedAlias}</span>
      <span className={styles.refCount}>
        <span className={styles.refNumber}>{referralCount}</span> referidos
      </span>
    </div>
  );
});

ReferralRow.displayName = "ReferralRow";

const SkeletonRow = memo(() => (
  <div
    className={styles.skeletonRow}
    aria-hidden="true"
  >
    <span className={`${styles.skeletonBlock} ${styles.skeletonRank}`} />
    <span className={`${styles.skeletonBlock} ${styles.skeletonAvatar}`} />
    <span className={`${styles.skeletonBlock} ${styles.skeletonName}`} />
    <span className={`${styles.skeletonBlock} ${styles.skeletonCount}`} />
  </div>
));

SkeletonRow.displayName = "SkeletonRow";

const ReferralList = memo(({referrers, onNavigate}) => {
  const shouldAnimate = referrers.length > ANIMATION_THRESHOLD;
  const trackClass = shouldAnimate ? `${styles.listTrack} ${styles.listTrackAnimated}` : styles.listTrack;

  const rows = referrers.map(referrer => (
    <ReferralRow
      key={referrer.alias}
      referrer={referrer}
      onNavigate={onNavigate}
    />
  ));

  return (
    <div className={styles.listWrapper}>
      <div className={trackClass}>
        {rows}
        {shouldAnimate &&
          referrers.map(referrer => (
            <ReferralRow
              key={`dup-${referrer.alias}`}
              referrer={referrer}
              onNavigate={onNavigate}
            />
          ))}
      </div>
    </div>
  );
});

ReferralList.displayName = "ReferralList";

const TopReferrals = () => {
  const navigate = useNavigate();
  const authStatus = useAuthStore(state => state.authStatus);
  const requireAuth = useRequireAuth();
  const {referrers, isPending} = useGetTopReferrals();
  const goToProfile = alias => requireAuth(() => navigate(`/u/${alias}`));

  const handleInvite = useCallback(() => {
    if (authStatus === "authenticated") {
      navigate("/user/referrals");
      return;
    }
    useAuthStore.getState().handleAuthForm("registration");
    useAuthStore.getState().handleShowAuthModal();
  }, [authStatus, navigate]);

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <span
          className={styles.headerIcon}
          role="img"
          aria-label="trophy"
        >
          🏆
        </span>
        <h3 className={styles.headerTitle}>Top Referidos</h3>
      </div>

      {isPending ? (
        <div
          className={styles.listWrapper}
          role="status"
          aria-busy="true"
          aria-label="Cargando top referidos"
        >
          {["a", "b", "c", "d", "e", "f"].map(id => (
            <SkeletonRow key={id} />
          ))}
        </div>
      ) : referrers.length === 0 ? (
        <div className={styles.emptyState}>Aún no hay referidos en el ranking</div>
      ) : (
        <ReferralList
          referrers={referrers}
          onNavigate={goToProfile}
        />
      )}

      <button
        type="button"
        className={styles.inviteButton}
        onClick={handleInvite}
        aria-label="Invitar a un amigo al programa de referidos"
      >
        Invitar a un amigo
      </button>
    </div>
  );
};

export default memo(TopReferrals);
